import { CustomCard } from "../../../Common/_components";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { IsoRangeDatePicker } from "../../../Everou/_components/IsoRangeDatePicker";
import { TimeHelper } from "../../../Everou/_helpers";
import { ReservationPublicUrl } from "./InvitationPublicUrl";
import { EditReservationButtons } from "./EditReservationButtons";
import { useCallback, useEffect, useState } from "react";
import { PMSLocationsActions } from "../../_actions/PMSLocationsActions";
import { PMSReservationsService } from "../../_services/PMSReservationsService";
import { Selectors } from "../../../Everou/_reducers/app.reducer";
import { canOfferEarlyCheckin, earlyCheckinOfferNotVisibleYet, getEarlyCheckinDecision, getReservationStateBadge } from "../../_models/PMSIntegration";
import { EarlyCheckinDecisionControls } from "../../_components/EarlyCheckinDecisionControls";
import { Icons } from "../../../Everou/_assets";
import { SimpleToggle } from "../../../Everou/_components";
import ToolTipWrapper from "../../../Everou/_components/ToolTipWrapper";
import { useGlobalNotification } from "../../../Common/_hooks/useGlobalNotification";
import { appPermissions } from "../../../Everou/_constants/permissions.constants";

export function ReservationInput( {
    reservation
}) {

    // LIBRARIES
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { notify } = useGlobalNotification();

    // GLOBAL STATE
    const [editable, setEditable] = useState(false);
    const timezone = TimeHelper.getCurrentTimezone();
    const locationInfo = useSelector(Selectors.getPMSLocationInfo);
    // Installers only see the reservation: editing and deleting is for the
    // owner and the admins, as in the backend.
    const canManage = appPermissions.canManageReservations(
        useSelector(Selectors.getPMSLocationSelectedPermissionType),
    );

    const [startDate, setStartDate] = useState(TimeHelper.serverStringToIsoString(reservation.reservationStart));
    const [endDate, setEndDate] = useState(TimeHelper.serverStringToIsoString(reservation.reservationEnd));

    // Offer state kept locally so toggling doesn't refetch the whole
    // reservations list (which would reset the module's filters).
    const [earlyCheckinOfferedLocal, setEarlyCheckinOfferedLocal] = useState(
        reservation.earlyCheckinOfferedAt,
    );
    const [offerBusy, setOfferBusy] = useState(false);
    const [earlyCheckinConfirmedLocal, setEarlyCheckinConfirmedLocal] = useState(
        reservation.earlyCheckinConfirmedAt,
    );
    const [earlyCheckinRejectedLocal, setEarlyCheckinRejectedLocal] = useState(
        reservation.earlyCheckinRejectedAt,
    );
    const [confirmBusy, setConfirmBusy] = useState(false);

    useEffect(() => {
        setStartDate(TimeHelper.serverStringToIsoString(reservation.reservationStart));
        setEndDate(TimeHelper.serverStringToIsoString(reservation.reservationEnd));
    }, [reservation]);

    useEffect(() => {
        setEarlyCheckinOfferedLocal(reservation.earlyCheckinOfferedAt);
    }, [reservation.earlyCheckinOfferedAt]);

    useEffect(() => {
        setEarlyCheckinConfirmedLocal(reservation.earlyCheckinConfirmedAt);
    }, [reservation.earlyCheckinConfirmedAt]);

    useEffect(() => {
        setEarlyCheckinRejectedLocal(reservation.earlyCheckinRejectedAt);
    }, [reservation.earlyCheckinRejectedAt]);

    async function handleOfferEarlyCheckin(enabled) {
        if (!reservation.invitationUid || offerBusy) return;
        setOfferBusy(true);
        try {
            const response = await PMSReservationsService.setEarlyCheckinOffer({
                uid: reservation.invitationUid,
                enabled,
            });
            setEarlyCheckinOfferedLocal(response?.early_checkin_offered_at ?? null);
        } catch (e) {
            // ServiceHelper already maps backend error codes (e.g. 770 when
            // the location toggle is off) to a translated message.
            notify(e?.message || t("global_error"), "error");
        } finally {
            setOfferBusy(false);
        }
    }

    async function handleEarlyCheckinDecision(decision) {
        if (!reservation.invitationUid || confirmBusy) return;
        setConfirmBusy(true);
        try {
            const response = await PMSReservationsService.setEarlyCheckinDecision({
                uid: reservation.invitationUid,
                decision,
            });
            setEarlyCheckinConfirmedLocal(response?.early_checkin_confirmed_at ?? null);
            setEarlyCheckinRejectedLocal(response?.early_checkin_rejected_at ?? null);
        } catch (e) {
            notify(e?.message || t("global_error"), "error");
        } finally {
            setConfirmBusy(false);
        }
    }

    // `reservation` must be a dependency: without it the callback keeps the
    // reservation from its first render, so reopening the editor after a save
    // reset the pickers to the pre-edit dates.
    const updateEdit = useCallback(() => {
        setEditable(!editable);
        setStartDate(TimeHelper.serverStringToIsoString(reservation.reservationStart));
        setEndDate(TimeHelper.serverStringToIsoString(reservation.reservationEnd));
    }, [editable, reservation]);

    const handleStartDate = useCallback((date) => {
        setStartDate(date);
    }, [setStartDate]);

    const handleEndDate = useCallback((date) => {
        setEndDate(date);
    }, [setEndDate]);
    

    const save = useCallback(() => {
        dispatch(PMSLocationsActions.updateReservation(
            reservation.uid,
            startDate,
            endDate
        ));

        setEditable(false);
    }, [dispatch, reservation, startDate, endDate]);

    // VIEW
    const badge = getReservationStateBadge(reservation);

    const pillStyle = {
        flexShrink: 0,
        color: "#fff",
        fontSize: "10px",
        padding: "1px 8px",
        borderRadius: "10px",
        whiteSpace: "nowrap",
    };

    const rowIconStyle = {
        width: "14px",
        height: "14px",
        opacity: 0.45,
        flexShrink: 0,
    };

    const formatRangeDate = (iso) => {
        if (!iso) return "—";
        return TimeHelper.localizeIsoStringToFormat(iso, timezone, "DD MMM HH:mm");
    };

    // Same visibility rule as the global reservations list; `offered` comes
    // from the local optimistic state so the toggle reacts without a refetch.
    const showEarlyCheckinOffer = canOfferEarlyCheckin(reservation, {
        offered: !!earlyCheckinOfferedLocal,
        locationEnabled: locationInfo.earlyCheckinEnabled,
    });
    // The public link only shows the offer card on the arrival day; tell the
    // host when the activated offer is stored but not yet visible.
    const offerNotVisibleYet =
        !!earlyCheckinOfferedLocal &&
        earlyCheckinOfferNotVisibleYet(reservation, locationInfo.timezone || timezone);

    // Request state sourced from the local optimistic decision fields; the
    // shared badge + controls render next to the requested hour.
    const { state: earlyCheckinState, canDecide: canDecideEarlyCheckin } =
        getEarlyCheckinDecision(reservation, {
            confirmedAt: earlyCheckinConfirmedLocal,
            rejectedAt: earlyCheckinRejectedLocal,
        });

    return (
        <CustomCard
            className="mb-2 c-hover-show-actions--visible"
            compact
            key={reservation.uid}
            style={{ borderLeft: `4px solid ${badge.color}`, maxWidth: "600px" }}
        >
            <div style={{ padding: "1px 4px" }}>
                <div
                    className="d-flex align-items-center flex-wrap"
                    style={{ columnGap: "8px", rowGap: "4px" }}
                >
                    <span className="font-weight-bold text-dark">
                        #{reservation.reservationId}
                        {reservation.isManual &&
                            <span className="font-weight-normal text-secondary">
                                {' '}&middot; {t('mod_pms_reservation_manual')}
                            </span>
                        }
                    </span>
                    <span style={{ ...pillStyle, backgroundColor: badge.color }}>
                        {t(badge.labelKey)}
                    </span>
                </div>
                {reservation.guestName &&
                    <div
                        className="text-dark text-truncate"
                        style={{ minWidth: 0 }}
                        title={reservation.guestName}
                    >
                        {reservation.guestName}
                    </div>
                }
                <div
                    className="text-secondary text-truncate"
                    style={{ minWidth: 0 }}
                    title={reservation.email}
                >
                    {reservation.email}
                </div>
            </div>

            {editable ? (
                <div className="mt-2">
                    <IsoRangeDatePicker
                        compact
                        onChangeStart={handleStartDate}
                        onChangeEnd={handleEndDate}
                        timezone={timezone}
                        begin={startDate}
                        end={endDate}
                        readOnly={!editable}
                        minDate={new Date()}
                        usePortal={true}
                    />
                </div>
            ) : (
                <div
                    className="text-secondary d-flex align-items-center flex-wrap mt-2"
                    style={{ padding: "1px 4px", columnGap: "8px" }}
                >
                    <img src={Icons.calendar} alt="" style={rowIconStyle} />
                    <span>{formatRangeDate(startDate)}</span>
                    <span className="text-dark">&rarr;</span>
                    <span>{formatRangeDate(endDate)}</span>
                </div>
            )}
            {reservation.earlyCheckinAt &&
                <div
                    className="text-secondary d-flex align-items-center flex-wrap mt-1"
                    style={{ padding: "1px 4px", columnGap: "8px" }}
                >
                    <img src={Icons.clock} alt="" style={rowIconStyle} />
                    <span className="c-text-capitalize-first">
                        {t('mod_pms_reservation_early_checkin')}
                    </span>
                    <span>
                        {TimeHelper.localizeIsoStringToFormat(
                            reservation.earlyCheckinAt,
                            locationInfo.timezone || timezone,
                            "HH:mm",
                        )}
                    </span>
                    {earlyCheckinState && (
                        <EarlyCheckinDecisionControls
                            state={earlyCheckinState}
                            canDecide={canDecideEarlyCheckin}
                            busy={confirmBusy}
                            onDecide={handleEarlyCheckinDecision}
                        />
                    )}
                </div>
            }
            {showEarlyCheckinOffer &&
                <div
                    className="text-secondary d-flex align-items-center mt-1"
                    style={{ padding: "1px 4px", columnGap: "8px" }}
                >
                    <span className="c-text-capitalize-first">
                        {t('mod_pms_reservation_early_checkin')}
                    </span>
                    <ToolTipWrapper tooltip={t("mod_pms_reservation_early_checkin_offer")}>
                        <SimpleToggle
                            testID="early-checkin-offer-toggle"
                            size="X_SMALL"
                            checked={!!earlyCheckinOfferedLocal}
                            readOnly={offerBusy}
                            handleChange={({ detail: { value } }) =>
                                handleOfferEarlyCheckin(value)
                            }
                        />
                    </ToolTipWrapper>
                    {offerNotVisibleYet && (
                        <span style={{ fontSize: "11px" }}>
                            {t("mod_pms_reservation_early_checkin_offer_visible_on_arrival")}
                        </span>
                    )}
                </div>
            }
            {reservation.earlyCheckoutAt &&
                <div
                    className="text-secondary d-flex align-items-center mt-1"
                    style={{ padding: "1px 4px", columnGap: "8px" }}
                >
                    <img src={Icons.clock} alt="" style={rowIconStyle} />
                    <span className="c-text-capitalize-first">
                        {t('mod_pms_reservation_early_checkout')}
                    </span>
                    <span>
                        {TimeHelper.localizeIsoStringToFormat(
                            reservation.earlyCheckoutAt,
                            locationInfo.timezone || timezone,
                            "HH:mm",
                        )}
                    </span>
                </div>
            }
            {reservation.keypadCode &&
                <div
                    className="text-secondary d-flex align-items-center mt-1"
                    style={{ padding: "1px 4px", columnGap: "8px" }}
                >
                    <img src={Icons.key} alt="" style={rowIconStyle} />
                    <span className="c-text-capitalize-first">
                        {t('mod_invitations_keypad_code')}
                    </span>
                    <span className="text-dark">{reservation.keypadCode}</span>
                </div>
            }
            {reservation.url &&
                <ReservationPublicUrl
                    compact
                    publicUrl={reservation.url}
                />
            }

            {editable ? (
                <div className="d-flex justify-content-end mt-2">
                    <EditReservationButtons
                        reservation={reservation}
                        showSaveButton={true}
                        handleEdit={updateEdit}
                        handleSave={save}
                    />
                </div>
            ) : canManage && (
                <div
                    className="c-hover-action"
                    style={{ position: "absolute", top: "6px", right: "6px" }}
                >
                    <EditReservationButtons
                        reservation={reservation}
                        showSaveButton={false}
                        handleEdit={updateEdit}
                        handleSave={save}
                    />
                </div>
            )}
        </CustomCard>
    )

}